'use client';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap, faPen, faSave } from '@fortawesome/sharp-solid-svg-icons';
import * as maptilersdk from '@maptiler/sdk';
import axios from 'axios';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import TopNavigation from '@/components/navigation/top-navigation';
import MapBox from '@/components/page-components/plat-map/mapbox';
import TabGroup from '@/components/tabs';
import {
  ACRES_ANNOUNCMENTS,
  AcresFeatureFlags,
  HTML_ELEMENTS,
} from '@/constants/constants';
import {
  CUSTOMIZE_LAND_PLAT_MAP_URL,
  PLAT_MAP_URL,
  PLAT_MAP_URL_LANDING,
  SAVED_SELECTIONS_URL,
} from '@/constants/urls';
import { getAcreCompsDetailsV2 } from '@/endpoints/itasca/comps/getAcreCompsDetails';
import {
  getInAppAnnoucements,
  InAppAnnouncementType,
} from '@/endpoints/itasca/in-app-announcements/getInAppAnnouncements';
import {
  getSharedSelections,
  ICorrectedSharedResponse,
} from '@/endpoints/itasca/land/share/getSharedSelection';
import DataResponse from '@/endpoints/types';
import ThreeDControllerProvider from '@/features/3d/3DControllerProvider';
import PlatMapAnnouncementModal from '@/features/announcements/platMapAnnouncementModal';
import CustomizeSettingsBox from '@/features/customizeMyLand/customizeSettingsBox';
import { PlatmapCustomizationReadonlyDialogBox } from '@/features/embed/readOnlyDialogBox/platMapCustomizationReadonlyDialogBox';
import { useInsightsCoverageContext } from '@/features/insightsCoverage/provider';
import SeoExpansion from '@/features/insightsCoverage/seoExpansion';
import { MobileNavigationWrapper } from '@/features/navigation/mobile-nav/mobileNavigationWrapper';
import { TabTypes, useNavigationContext } from '@/features/navigation/provider';
import { OnMapSearch } from '@/features/onMapSearch/onMapSearch';
import { SelectionsPanel } from '@/features/selectionsPanel/selectionsPanel';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';
import { OldSelectionsPanel } from '@/features/selectionsPanelOld/selectionsPanel';
import { ToolNoneDrawModes } from '@/features/toolbar/constants';
import { useToolbarContext } from '@/features/toolbar/provider';
import useDraw from '@/hooks/draw';
import useBreakpoints from '@/hooks/useBreakpoint';
import { useGetFeatureFlag } from '@/hooks/useGetFeatureFlag';
import { AcreCompsResponseDetails } from '@/models/comps';
import { AcresFeature, FeatureType, IParcelFeature } from '@/models/features';
import {
  CompSourcesEnumsBE,
  IAcresCompFeature,
  IInternalCompFeature,
} from '@/models/features/comp';
import {
  isCompFeature,
  isCourthouseCompFeature,
  isInternalCompFeature,
} from '@/models/features/guards';
import ActiveTabTitle from '@/models/navigation/activeTab';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/auth-provider';
import { useInitializeContext } from '@/providers/InitializeProvider';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import { useModalContext } from '@/providers/ModalProvider';
import { setBoundaryTransfer } from '@/redux/slices/boundaryTransfer';
import { RootState } from '@/redux/store';
import Logger from '@/utils/logger';

import BottomNavigation from '../../../features/navigation/bottom-navigation';
import SideNav from '../../../features/navigation/side-nav';

export const mobileTabs = [
  {
    label: <FontAwesomeIcon icon={faMap} />,
    value: 'map',
  },
  {
    label: <FontAwesomeIcon icon={faPen} />,
    value: 'customize',
  },
  {
    disable: true,
    label: <FontAwesomeIcon icon={faSave} />,
    value: 'portfolio',
  },
];

const routesToHideSideNav = [CUSTOMIZE_LAND_PLAT_MAP_URL];

/**
 * TODO: With app router, convert this to a layout.tsx file, in /pages/plat-map/
 */
export const PlatmapLayout = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const pathname = usePathname();
  const query = useSearchParams();
  const dispatch = useDispatch();
  const { trackLayerSelected } = useAnalytics();
  const { currentMobileTab, setCurrentMobileTab, getTabMap, aerialMapIsOpen } =
    useNavigationContext();
  const { getFeature } = useInitializeContext();

  const isNewSelectionsPanelActive = getFeature(
    AcresFeatureFlags.TEMP_NEW_SELECTIONS_PANEL
  );
  const isToolbarActive = useGetFeatureFlag(
    AcresFeatureFlags.TEMP_TOOL_TOP_BAR
  );
  const { showViewOnlyCustomizeBox, selectedTool } = useToolbarContext();

  const { isAnonymous } = useAuth();

  const { insightsCoverage } = useInsightsCoverageContext();
  const { hasSelectedFeature } = useSelectionsPanelContext();
  const {
    title,
    body,
    stateCounties,
    countyInfo,
    stateInfo,
    countiesWithListings,
    countiesWithSoldLand,
  } = insightsCoverage;

  const showSearchBar = useMemo(() => {
    return Boolean(
      !aerialMapIsOpen && pathname?.startsWith(PLAT_MAP_URL_LANDING)
    );
  }, [aerialMapIsOpen, pathname]);

  const showTools = useMemo(() => {
    return (
      !!getFeature(AcresFeatureFlags.TEMP_TOOL_TOP_BAR) && !aerialMapIsOpen
    );
  }, [aerialMapIsOpen, getFeature]);

  const showSideNav = useMemo(() => {
    if (!pathname) return false;
    return (
      !aerialMapIsOpen &&
      !routesToHideSideNav.some((route) => pathname.includes(route))
    );
  }, [aerialMapIsOpen, pathname]);

  const handleSettingTab = useCallback(
    (tab: string) => {
      if (tab === currentMobileTab) {
        setCurrentMobileTab(undefined);

        return;
      }

      if (tab === TabTypes.CUSTOMIZE) {
        router.push(CUSTOMIZE_LAND_PLAT_MAP_URL);
      }

      if (tab === TabTypes.PORTFOLIO) {
        router.push(SAVED_SELECTIONS_URL);
      }

      setCurrentMobileTab(tab);
    },
    [currentMobileTab, router, setCurrentMobileTab]
  );

  const { allFeatures, parcels } = useSelector(
    (state: RootState) => state.features
  );

  const { mapInstance: map } = useMapboxContext();

  useDraw();

  const { isMobile } = useBreakpoints();

  const [calledReplace, setCalledReplace] = useState<boolean>(false);
  const [shared, setShared] = useState<
    ICorrectedSharedResponse | null | undefined
  >(null);

  const cancelToken = useMemo(() => axios.CancelToken.source(), []);
  const { setModalContent, clearModalContent } = useModalContext();

  const fetchAnnouncements = useCallback(async () => {
    //  Gets annoucements from backend
    const { res, err } = await getInAppAnnoucements(cancelToken.token);

    if (!res || err || !res.data) {
      return;
    }
    const announcements = res.data;

    //  This grabs the first annoucement that is this type, we wont ever show
    //  multiple annoucements at the same time.
    const platMapAnnouncement = announcements.find(
      (annoucement) => annoucement.type === InAppAnnouncementType.PLATMAP_MODAL
    );

    //  If no announcement to show, return
    if (!platMapAnnouncement) {
      return;
    }

    const oldAnnouncementsString = localStorage.getItem(ACRES_ANNOUNCMENTS);

    const oldAnnouncements: any[] = oldAnnouncementsString
      ? JSON.parse(oldAnnouncementsString)
      : [];

    const oldAnnouncementIndex = oldAnnouncements.findIndex(
      (a) => a.codename === platMapAnnouncement.codename
    );

    // If the days hidden field is null, the announcement should only get shown once
    if (
      oldAnnouncementIndex > -1 &&
      platMapAnnouncement.days_hidden_after_initial_view === null
    ) {
      return;
    }

    // Don't show a repeat announcement if it hasn't been the "days_hidden_after_initial_view" time yet
    const shownDate = Date.now();
    if (
      platMapAnnouncement.days_hidden_after_initial_view &&
      oldAnnouncementIndex > -1
    ) {
      const isBeforeDurr = dayjs(shownDate).isBefore(
        dayjs(oldAnnouncements[oldAnnouncementIndex].shownDate).add(
          platMapAnnouncement.days_hidden_after_initial_view,
          'day'
        )
      );

      // If the announcement has already been shown within the duration then don't
      //  show it again, if its after the duration show the announcement
      if (isBeforeDurr) return;
    }

    setModalContent(
      <PlatMapAnnouncementModal
        ctaOneLabel={platMapAnnouncement.cta_1_label}
        ctaOneUrl={platMapAnnouncement.cta_1_url}
        ctaTwoLabel={platMapAnnouncement.cta_2_label}
        ctaTwoUrl={platMapAnnouncement.cta_2_url}
        disclaimer={platMapAnnouncement.disclaimer}
        subTitle={platMapAnnouncement.subtitle}
        title={platMapAnnouncement.title}
        onClose={() => {
          clearModalContent();
          const newAnnouncements = oldAnnouncements.filter(
            (a) => a.codename !== platMapAnnouncement.codename
          );
          localStorage.setItem(
            ACRES_ANNOUNCMENTS,
            JSON.stringify([
              ...newAnnouncements,
              {
                ...platMapAnnouncement,
                shownDate,
              },
            ])
          );
        }}
      />
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelToken.token, setModalContent, clearModalContent]); // Don't add router to deps

  useEffect(() => {
    if (pathname?.includes(PLAT_MAP_URL_LANDING)) {
      fetchAnnouncements();
    }
  }, [pathname]);

  const handleShareSelection = async (sharecode: string) => {
    const { res, err } = await getSharedSelections(sharecode);
    if (err) {
      return null;
    }

    return res?.data;
  };

  useEffect(() => {
    const sharecode = query?.get('sharecode');

    if (sharecode && typeof sharecode === 'string') {
      handleShareSelection(sharecode).then((value) => {
        setShared(value);
      });
    }
  }, [query]);

  const handlePermsForBoundary = useCallback(
    async (boundarySet: AcresFeature[]) => {
      setCalledReplace(true);

      const userComps = boundarySet.filter((feature) => {
        if (!feature.properties) return false;
        if (
          !(
            isCompFeature(feature) ||
            isInternalCompFeature(feature) ||
            isCourthouseCompFeature(feature)
          )
        )
          return false;
        if (!feature.properties.id && !feature.id) return false;
        if (
          (feature.properties.type === FeatureType.COMP ||
            feature.properties.type === FeatureType.INTERNAL_COMP) &&
          feature.properties.visibility !== CompSourcesEnumsBE.COURTHOUSE
        ) {
          return true;
        }

        return false;
      });

      const userCompsPromise = userComps.map(
        (userComp: IAcresCompFeature | IInternalCompFeature) => {
          return new Promise((res, rej) => {
            try {
              getAcreCompsDetailsV2(
                undefined,
                // Properties ID is the ID of the comp.  The top level ID can be the ID in the copy saved table
                (userComp.properties.source_id || userComp.properties.id) ??
                  (userComp.id as number | string)
              ).then((response) => {
                res({ id: userComp.properties.id, response });
              });
            } catch {
              rej(new Error('Failed to fetch comp'));
            }
          });
        }
      );

      const userCompsRes = await Promise.all(userCompsPromise);

      const compsToHide = userCompsRes.filter(
        (compRes: {
          response?: DataResponse<AcreCompsResponseDetails>;
          id: number;
        }) => !compRes.response || compRes.response.err
      );

      const filteredBoundary = boundarySet.filter((feature) => {
        if (
          !(
            isCompFeature(feature) ||
            isInternalCompFeature(feature) ||
            isCourthouseCompFeature(feature)
          )
        )
          return true;

        if (
          compsToHide.some(
            (comp: {
              response?: DataResponse<AcreCompsResponseDetails>;
              id: number;
            }) => comp.id === feature.properties.id
          )
        )
          return false;

        return true;
      });

      const filteredBoundaryRes = filteredBoundary.map((boundary) => {
        const parcelTyped = boundary as IParcelFeature;
        if (!parcelTyped.properties?.clip) return parcelTyped;

        try {
          return {
            ...parcelTyped,
            properties: {
              ...parcelTyped.properties,
              type: FeatureType.PARCEL,
            },
          };
        } catch (err) {
          Logger.error(err);
          return parcelTyped;
        }
      });

      dispatch(setBoundaryTransfer(filteredBoundaryRes));
      router.replace(PLAT_MAP_URL);
    },
    [dispatch, router]
  );

  useEffect(() => {
    if (shared && !calledReplace && pathname?.includes(PLAT_MAP_URL)) {
      handlePermsForBoundary(shared.boundary_set);
    }
  }, [shared, calledReplace, handlePermsForBoundary, pathname]);

  useEffect(() => {
    if (!pathname?.includes('/plat-map')) {
      return;
    }
    const parts = pathname?.split('/');
    const layer = parts?.[2];

    // Don't want to track if layer is a wildcard
    if (!layer || layer.includes('[')) return;
    trackLayerSelected(layer as ActiveTabTitle, allFeatures, parcels);
  }, [pathname]);

  const tabs = useMemo(() => Object.values(getTabMap(true)), [getTabMap]);
  const showReadOnlyCustomizationDialog = useMemo(() => {
    return (
      !pathname?.includes(CUSTOMIZE_LAND_PLAT_MAP_URL) &&
      pathname?.includes(PLAT_MAP_URL) &&
      !aerialMapIsOpen
    );
  }, [aerialMapIsOpen, pathname]);

  if (isMobile) {
    return (
      <ThreeDControllerProvider map={map as maptilersdk.Map | undefined}>
        <div className="relative flex h-[100dvh] w-screen flex-col sm:hidden">
          <TopNavigation />
          <MobileNavigationWrapper
            isMobilePanelOpen={
              currentMobileTab === 'map' || currentMobileTab === 'customize'
            }
          >
            {children}
          </MobileNavigationWrapper>
          <TabGroup
            activeValue={currentMobileTab}
            tabs={tabs}
            onChange={handleSettingTab}
          />
        </div>
      </ThreeDControllerProvider>
    );
  }

  return (
    <ThreeDControllerProvider map={map as maptilersdk.Map | undefined}>
      <div className="relative hidden h-screen w-screen flex-col sm:flex">
        <div className="relative flex h-full overflow-hidden">
          {showSideNav && (
            <div className="z-[160]">
              <SideNav />
            </div>
          )}

          {/* Rest of app */}
          <div className="z-0 flex h-full w-full flex-col">
            {/* Report panel and map */}
            <div className="relative flex h-full w-full">
              {/* Report Panel */}
              {/* Map is the only page that DOESN't have the report panel present */}
              {pathname !== '/plat-map/map' ? (
                <AnimatePresence initial>
                  <motion.div
                    key="content"
                    animate="open"
                    className={twMerge(
                      'grow-1 z-[162] flex bg-white',
                      aerialMapIsOpen ? 'hidden' : '' // Hide whatever is open when aerial map is open. This allows whatever layer is opened to keep it on the map
                    )}
                    exit="closed"
                    id={HTML_ELEMENTS.INSIGHTS_CONTAINER}
                    initial="closed"
                    variants={{
                      closed: {
                        transition: {
                          duration: 0.1,
                          ease: 'easeInOut',
                        },
                        x: '-100%',
                      },
                      open: {
                        transition: {
                          duration: 0.1,
                          ease: 'easeIn',
                        },
                        x: 0,
                      },
                    }}
                  >
                    {children}
                  </motion.div>
                </AnimatePresence>
              ) : (
                <>{children}</>
              )}
              <div className="relative flex grow flex-col">
                <div className="pointer-events-none z-[400] flex max-h-full">
                  <OnMapSearch
                    showSearch={showSearchBar}
                    showTools={showTools}
                  />
                </div>

                {(showReadOnlyCustomizationDialog && !isToolbarActive) ||
                (showViewOnlyCustomizeBox &&
                  selectedTool.mode === ToolNoneDrawModes.Info_Tool) ? (
                  <PlatmapCustomizationReadonlyDialogBox className="absolute top-[62px] z-[200] m-4" />
                ) : (
                  <CustomizeSettingsBox className="absolute right-[263px] z-[200] m-3" />
                )}
                <div className="absolute bottom-0 left-0 z-[200]">
                  <BottomNavigation map={map} />
                </div>
              </div>

              <div className="fixed right-0 top-0 z-[200] flex h-min flex-col sm:right-[0] sm:w-[264px]">
                {!hasSelectedFeature && (
                  <SeoExpansion
                    adjacentCounties={countyInfo?.adjacent_counties}
                    body={body}
                    className="absolute left-0 top-0 z-[201] h-screen w-full"
                    countiesWithListings={countiesWithListings}
                    countiesWithSoldLand={countiesWithSoldLand}
                    countyInfo={countyInfo}
                    stateCounties={stateCounties}
                    stateInfo={stateInfo}
                    title={title}
                  />
                )}
                {isNewSelectionsPanelActive &&
                  (!isAnonymous || hasSelectedFeature) && <SelectionsPanel />}
                {!isNewSelectionsPanelActive && hasSelectedFeature && (
                  <OldSelectionsPanel />
                )}
              </div>
            </div>

            {/* The MAP */}
            <div className={twMerge('absolute left-0 flex h-full w-full grow')}>
              <MapBox />
            </div>
          </div>
        </div>
      </div>
    </ThreeDControllerProvider>
  );
};
